import { z } from 'zod';
import { isoTimestamp } from './common.js';
/**
 * The weather shown on the dashboard (the home screen).
 *
 * Sourced from Open-Meteo through a **server proxy** — the browser cannot call
 * it directly, because the CSP pins `connect-src` to `'self'` (see the
 * Caddyfile) and that stays that way. The server fetches the household's fixed
 * location once, normalises it into this shape and validates it on the way out,
 * so a change in Open-Meteo's payload fails here rather than as `undefined` on
 * the screen.
 *
 * Units are Open-Meteo's own for a metric locale: temperatures in °C, wind in
 * km/h, humidity in %, precipitation in mm. `weather_code` is a WMO code the
 * client maps to a Dutch label and an icon (`web/src/weather/codes.tsx`).
 */
export const weatherCurrent = z.object({
    temperature: z.number(),
    apparent_temperature: z.number(),
    humidity: z.number(),
    precipitation: z.number(),
    wind_speed: z.number(),
    /**
     * WMO code for the conditions **right now** — drives the headline icon and
     * label. Distinct from `weatherDay.weather_code`, which is a whole-day
     * aggregate: on a day that rained earlier the day code reads "rain" while the
     * current sky is dry, so the headline must use this, not `days[0]`.
     */
    weather_code: z.number().int(),
});
export const weatherDay = z.object({
    /** Local calendar date, `YYYY-MM-DD`. */
    date: z.string(),
    weather_code: z.number().int(),
    temp_max: z.number(),
    temp_min: z.number(),
    precipitation_sum: z.number(),
    /**
     * Sunset for this day, local time `YYYY-MM-DDTHH:mm` (no offset — Open-Meteo
     * returns it in the requested zone, like the hourly times). The dashboard reads
     * today's off `days[0]` and shows just the `HH:mm`; carried per day because
     * that is the shape Open-Meteo gives it and the value is genuinely per-day.
     */
    sunset: z.string(),
});
export const weatherHour = z.object({
    /** Local time on the hour, `YYYY-MM-DDTHH:mm`; the first entry is the current hour. */
    time: z.string(),
    /** Rain forecast for the hour, in mm. */
    precipitation: z.number(),
    /** Chance of precipitation in the hour, 0–100. */
    precipitation_probability: z.number(),
});
/**
 * A 5-minute radar bucket from Buienradar's nowcast — its `time` an **absolute**
 * UTC instant (unlike the Open-Meteo fields above, which are local wall-clock),
 * because the caller resolves the dateless local stamps to real instants first.
 * `precipitation` is rain **intensity in mm/h**, not an hourly total.
 */
export const precipBucket = z.object({
    time: isoTimestamp,
    precipitation: z.number(),
});
export const weatherResponse = z.object({
    current: weatherCurrent,
    /** The coming days, soonest first — the dashboard shows the next three. */
    days: z.array(weatherDay),
    /** The coming hours from now, soonest first — for the "will it rain?" strip and summary. */
    hourly: z.array(weatherHour),
    /**
     * Buienradar's per-location **radar nowcast** — 5-minute buckets (mm/h) for the
     * next ~2 hours. This is what the card answers "regent het / gaat het regenen?"
     * from, *not* the Open-Meteo `hourly` above: radar extrapolation catches the
     * light, scattered showers the NWP model reports as a flat 0.0 mm (the exact
     * reason the push alert already reads it, `api/src/weather/notify.ts`). **Empty
     * when Buienradar is unreachable**, and the card then falls back to the hourly
     * outlook — the sky headline and 3-day view stay on Open-Meteo either way.
     */
    radar: z.array(precipBucket),
    fetched_at: isoTimestamp,
});
//# sourceMappingURL=weather.js.map